import { WATTS_IN_MW } from '@/constants/units'
import type { ExtendedBid, PtuChunk } from '@/features/bidding/types/bid'
import { roundWattsToNearest10KW } from '@/features/bidding/utils/calculations/convertToRoundedMw'
import { decimalAdjust } from '@/features/bidding/utils/calculations/decimalAdjust'
import type { AggregatedBidWrapper } from '@/features/bidding/utils/model/mapActivationGroupBidsToAggregatedBids'
import type { PriceQuantity } from '@/features/bidding/utils/price/getPtuPriceQuantitiesFromBids'

/**
 * Updates the original ExtendedBid objects with new price chunks from the aggregated bids.
 *
 * This function modifies the existing bids in place by:
 * 1. Finding the corresponding aggregated bid for each activation group
 * 2. Mapping the aggregated bid's price quantities to PTU chunks
 * 3. Updating the ptuChunks field while preserving other bid properties
 *
 * @param existingBids - The original bids with prices
 * @param modifiedBids - Map of activation group UUID to modified aggregated bid wrapper
 * @returns The updated ExtendedBid array
 */
export function updateExtendedBidsBasedOnAggregatedBids(
  existingBids: ExtendedBid[],
  modifiedBids: Map<string, AggregatedBidWrapper>,
): ExtendedBid[] {
  for (const bid of existingBids) {
    // There is only one aggregated bid for each activation group bid
    const aggregatedBid = modifiedBids.get(bid.activationGroupUuid)!.aggregatedBid

    // The only thing from the bid that the user is modifying is the ptu chunks
    bid.ptus = bid.ptus.map((existingPtu) => {
      const modifiedPtu = aggregatedBid.ptus.find((ptu) => existingPtu.ptu.start.equals(ptu.ptuStart))

      if (!modifiedPtu) {
        throw new Error(`No modified PTU found for PTU ${existingPtu.ptu.start}`)
      }

      return {
        ptu: existingPtu.ptu,
        offeredVolume: existingPtu.offeredVolume, // Total volume for this PTU might not equal the sum of the chunks but the backend will fix it
        acceptedVolume: existingPtu.acceptedVolume,
        ptuChunks: modifiedPtu.priceQuantities.map((priceQuantity) =>
          buildPtuChunk(priceQuantity, existingPtu.offeredVolume.unit),
        ),
      }
    })
  }

  return existingBids
}

/**
 * Converts a price quantity (in MW/MWh) to a PTU chunk (in Watts/Watt-hours).
 *
 * Performs the following conversions:
 * 1. Converts MW/MWh to Watts/Watt-hours
 * 2. Rounds to 2 decimal places
 * 3. Rounds to nearest 10kW
 *
 * @param priceQuantity - The price and quantity in MW/MWh
 * @param volumeUnit - The unit to use for the volume (e.g., "WATTS", "WATT_HOURS")
 * @returns PTU chunk with converted volume and price
 */
function buildPtuChunk(priceQuantity: PriceQuantity, volumeUnit: string): PtuChunk {
  const newCapacityInWatts = decimalAdjust('round', priceQuantity.quantity * WATTS_IN_MW, -2)
  const roundedNewCapacityInWatts = roundWattsToNearest10KW(newCapacityInWatts)

  return {
    offeredVolume: { unit: volumeUnit, quantity: roundedNewCapacityInWatts },
    offeredPrice: priceQuantity.price,
  }
}
